import styled from 'styled-components'
import { t } from 'ttag'
import { useCallback, useEffect, useContext } from 'react'

import { features } from '@h2/components/media'
import { PG, SLB, NAVIANCEBLUE } from '@h2/components/colors'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Field } from '@h2/components/inputs/field'
import { TextInput } from '@h2/components/inputs/text'
import { useForm } from '@h2/common/components/hooks/useForm'
import { Card } from '@h2/common/components/card'
import { Globals } from '@h2/globals'

import { props } from './props'

import { Explorer } from '../highlights/explorer'
import { Navigator } from '../highlights/navigator'
import { Controls } from '../controls'
import { Submit } from '../submit'
import { Back } from '../back'

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;

  > h2 {
    font-size: 24px;
    line-height: 28px;
  }

  > div:nth-child(n + 2) {
    margin: 15px 0 0 0;
  }

  > div:nth-last-child(2) {
    margin: 15px 0;
  }

  > div:last-child {
    margin-top: auto;
  }

  @media (${features.minWidth.laptop}) {
    > h2 {
      font-size: 30px;
      line-height: 34px;
    }
  }
`
const OptionalText = styled(Text)`
  display: inline;
`
const About = ({
  inDialog,
  properties,
  validate,
  isValidating,
  formErrors,
  renderPanel
}) => {
  const { isNavianceUser } = useContext(Globals)
  const {
    isNavigator,
    isSSO,
    firstName,
    lastName,
    password,
    noEmail,
    isExplorer,
    isBelow13
  } = properties
  const { register, error, handleOnSubmit, isInvalid, setValidation, clearErrors } =
    useForm({
      validators: {
        firstName(form) {
          if (isNavigator && !form.firstName?.trim()) {
            return t`First Name is required`
          }
        },
        lastName(form) {
          if (isNavigator && !form.lastName?.trim()) {
            return t`Last Name is required`
          }
        },
        password(form) {
          if (!isSSO) {
            if (form.password === form.confirm && error.confirm) {
              clearErrors('confirm')
            }

            if (!form.password?.trim()) {
              return t`Password is required`
            }

            if (form.password.trim().length < 6) {
              return t`Password must be at least 6 characters`
            }
          }
        },
        confirm(form) {
          if (!isSSO) {
            if (form.password && form.confirm !== form.password) {
              return t`Passwords do not match`
            }
          }
        }
      },
      initialValues: {
        firstName,
        lastName,
        password
      }
    })
  const onSubmit = useCallback(form => validate(form), [validate])
  const onBack = useCallback(() => {
    if (noEmail) {
      return renderPanel('username')
    }

    return isNavianceUser || isBelow13 ? renderPanel('email') : renderPanel('mainType')
  }, [noEmail, isNavianceUser, isBelow13, renderPanel])

  useEffect(() => {
    if (formErrors) {
      setValidation(formErrors)
    }
  }, [formErrors, setValidation])

  const isDisabled = isInvalid || isValidating

  return (
    <Card inDialog={inDialog}>
      {isExplorer ? (
        <Card.Highlight color={isNavianceUser ? NAVIANCEBLUE : PG}>
          <Explorer />
        </Card.Highlight>
      ) : (
        <Card.Highlight color={SLB}>
          <Navigator />
        </Card.Highlight>
      )}
      <Card.Content>
        <Form id="reg-about" onSubmit={handleOnSubmit(onSubmit)}>
          <Heading level="2" align="center" mb="12px">
            {isNavianceUser ? t`Set up your account` : t`About you`}
          </Heading>
          <Text align="center" margin="0">
            {!isNavianceUser && t`Tell us a little about yourself to setup your account.`}
          </Text>
          <Field
            isRequired={isNavigator}
            width="100%"
            label={(
              <>
                {t`First Name`}
                {!isNavigator && <OptionalText> {t`(optional)`}</OptionalText>}
              </>
            )}
            desc={error.firstName}
            status={error.firstName ? 'error' : 'default'}>
            <TextInput
              size="small"
              {...register('firstName', { required: isNavigator })}
            />
          </Field>
          {!isBelow13 && (
          <Field
            isRequired={isNavigator}
            width="100%"
            label={(
              <>
                {t`Last Name`}
                {!isNavigator && <OptionalText> {t`(optional)`}</OptionalText>}
              </>
            )}
            desc={error.lastName}
            status={error.lastName ? 'error' : 'default'}>
            <TextInput
              size="small"
              {...register('lastName', { required: isNavigator })}
            />
          </Field>
          )}
          {!isSSO && (
            <>
              <Field
                isRequired
                width="100%"
                label={t`Password`}
                desc={error.password}
                status={error.password ? 'error' : 'default'}>
                <TextInput
                  size="small"
                  type="password"
                  {...register('password', { required: true })}
                />
              </Field>
              <Field
                isRequired
                width="100%"
                label={t`Confirm Password`}
                desc={error.confirm}
                status={error.confirm ? 'error' : 'default'}>
                <TextInput
                  size="small"
                  type="password"
                  {...register('confirm', { required: true })}
                />
              </Field>
            </>
          )}
          <Controls>
            <Back onClick={onBack} />
            <Submit text={t`Next`} isLoading={isValidating} isDisabled={isDisabled} />
          </Controls>
        </Form>
      </Card.Content>
      <Card.Detour>
        <Back onClick={onBack} />
        <Submit
          text={t`Next`}
          form="reg-about"
          isLoading={isValidating}
          isDisabled={isDisabled}
        />
      </Card.Detour>
    </Card>
  )
}

About.propTypes = { ...props }

export { About }
